import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const AdminForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Reset Password | Hadassah Homes Admin";
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/admin/reset-password`,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not send reset email");
      return;
    }
    setSent(true);
    toast.success("Reset email sent");
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0F1F18] px-5 py-16">
      <div className="w-full max-w-md border border-white/10 bg-white p-8 md:p-10">
        <div className="mb-7">
          <p className="font-body text-[10px] uppercase tracking-[0.28em] text-accent">
            Hadassah Homes
          </p>
          <h1 className="mt-2 font-headline text-3xl text-foreground">Reset password</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Enter the email associated with your admin account and we will send you a secure link
            to set a new password.
          </p>
        </div>

        {sent ? (
          <div className="space-y-5">
            <div className="border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-800">
              If an account exists for <span className="font-medium">{email}</span>, a password
              reset link has been sent. Check your inbox (and spam folder).
            </div>
            <Link
              to="/admin/login"
              className="inline-flex items-center gap-1.5 text-sm text-accent hover:text-accent/80"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">
                Email
              </label>
              <Input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none"
              />
            </div>

            <Button
              type="submit"
              variant="gold"
              size="lg"
              disabled={submitting}
              className="w-full rounded-none"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
            </Button>

            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <Link
                to="/admin/login"
                className="inline-flex items-center gap-1.5 hover:text-foreground"
              >
                <ArrowLeft className="h-3 w-3" /> Back to sign in
              </Link>
              <span className="inline-flex items-center gap-1.5">
                <Lock className="h-3 w-3" /> Secured by Supabase Auth
              </span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminForgotPassword;
